import React from "react";
import { useNoIndex } from "@/lib/usePageSeo";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

// Estimate confirmation surface (Feb 2026). Mirrors the visual language
// of CheckoutResultPage.jsx (icon disc, headline, supporting copy,
// reference-id card, Back to Home) BUT deliberately strips every
// payment / Sentoo / paid-order concept — no "Order received", no
// payment status, no "Due Now", no test-mode banner. An estimate
// request is not a payment; it's a written request for a written quote.
//
// The page is read-only: it renders the request_id passed via the URL
// (?request_id=<uuid>) but never re-POSTs to the backend. That makes
// browser refresh safe (no duplicate submission) and browser Back a
// no-op — the caller navigates here with `replace: true` so the
// customer cannot walk back into the Review step and hit the submit
// button again.
export default function EstimateResultPage() {
  useNoIndex();
  const [params] = useSearchParams();
  const requestId = params.get("request_id");
  const serviceName = params.get("service_name");

  return (
    <div data-testid="estimate-result" className="bg-[#0F0F0F] min-h-[60vh]">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-24">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full mx-auto grid place-items-center bg-[#FF6600]/20 border border-[#FF6600]">
            <CheckCircle2 className="w-9 h-9 text-[#FF6600]" />
          </div>
          <h1
            className="font-display mt-6 text-4xl sm:text-5xl font-black tracking-tight"
            data-testid="estimate-result-headline"
          >
            Estimate request received
          </h1>
          <p className="mt-4 text-[#B5B5B5]">
            We&rsquo;ve received your project details. Our team will review your brief and contact you with a tailored estimate.
          </p>
        </div>

        {(requestId || serviceName) && (
          <div className="mt-8 ur-card p-6" data-testid="estimate-result-card">
            {serviceName && (
              <>
                <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">Service</div>
                <div className="text-white text-sm font-semibold mt-1" data-testid="estimate-result-service-name">
                  {serviceName}
                </div>
              </>
            )}
            {requestId && (
              <>
                <div className={`text-xs uppercase tracking-widest text-[#A3A3A3] ${serviceName ? "mt-5" : ""}`}>
                  Reference
                </div>
                <div className="font-mono text-sm mt-1" data-testid="estimate-result-request-id">
                  {requestId}
                </div>
              </>
            )}
            <div className="mt-5 grid sm:grid-cols-1 gap-3">
              <div className="rounded-md border border-[#262626] px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Status</div>
                <div
                  className="text-white mt-1 text-sm font-semibold"
                  data-testid="estimate-result-status"
                >
                  Estimate request received
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center flex flex-col sm:flex-row justify-center items-center gap-3 mt-10">
          <Link
            to="/"
            className="ur-btn-primary"
            data-testid="estimate-result-home"
          >
            Back to Home <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/services"
            className="ur-btn-secondary"
            data-testid="estimate-result-view-services"
          >
            View Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
